import type { SuggestionKeyDownProps, SuggestionProps } from '@tiptap/suggestion';

export interface SuggestionMenuItem {
   id: string;
   title: string;
   subtitle?: string;
   icon?: string;
}

export const createSuggestionMenu = <Item extends SuggestionMenuItem>() => {
   let element: HTMLDivElement | null = null;
   let currentProps: SuggestionProps<Item, Item> | null = null;
   let selectedIndex = 0;
   let unmount: (() => void) | null = null;

   const selectItem = (index: number) => {
      const item = currentProps?.items[index];
      if (item) {
         currentProps?.command(item);
      }
   };

   const renderItems = () => {
      if (!element || !currentProps) return;

      element.replaceChildren();

      if (currentProps.items.length === 0) {
         const empty = document.createElement('p');
         empty.className = 'editor-suggestion-empty';
         empty.textContent = 'No matches';
         element.appendChild(empty);
         return;
      }

      currentProps.items.forEach((item, index) => {
         const button = document.createElement('button');
         button.type = 'button';
         button.className = `editor-suggestion-item${
            index === selectedIndex ? ' is-selected' : ''
         }`;
         button.addEventListener('mousedown', (event) => {
            event.preventDefault();
            selectItem(index);
         });

         if (item.icon) {
            const icon = document.createElement('span');
            icon.className = 'editor-suggestion-icon';
            icon.textContent = item.icon;
            button.appendChild(icon);
         }

         const copy = document.createElement('span');
         copy.className = 'editor-suggestion-copy';

         const title = document.createElement('span');
         title.className = 'editor-suggestion-title';
         title.textContent = item.title;
         copy.appendChild(title);

         if (item.subtitle) {
            const subtitle = document.createElement('span');
            subtitle.className = 'editor-suggestion-subtitle';
            subtitle.textContent = item.subtitle;
            copy.appendChild(subtitle);
         }

         button.appendChild(copy);
         element?.appendChild(button);
      });
   };

   const update = (props: SuggestionProps<Item, Item>) => {
      currentProps = props;
      selectedIndex = Math.min(selectedIndex, Math.max(props.items.length - 1, 0));
      renderItems();
   };

   return {
      onStart: (props: SuggestionProps<Item, Item>) => {
         element = document.createElement('div');
         element.className = 'editor-suggestion-menu';
         selectedIndex = 0;
         update(props);
         unmount = props.mount(element);
      },
      onUpdate: (props: SuggestionProps<Item, Item>) => {
         selectedIndex = 0;
         update(props);
      },
      onKeyDown: ({ event }: SuggestionKeyDownProps) => {
         const itemCount = currentProps?.items.length || 0;

         if (event.key === 'ArrowUp' && itemCount > 0) {
            selectedIndex = (selectedIndex + itemCount - 1) % itemCount;
            renderItems();
            return true;
         }

         if (event.key === 'ArrowDown' && itemCount > 0) {
            selectedIndex = (selectedIndex + 1) % itemCount;
            renderItems();
            return true;
         }

         if (event.key === 'Enter' && itemCount > 0) {
            selectItem(selectedIndex);
            return true;
         }

         return event.key === 'Escape';
      },
      onExit: () => {
         unmount?.();
         unmount = null;
         element = null;
         currentProps = null;
      },
   };
};
